
import { useState, useEffect } from 'react';
import Button from '../../../components/base/Button';
import Input from '../../../components/base/Input';
import Modal from '../../../components/base/Modal';

interface User {
  id: string;
  email: string;
  full_name: string;
  role: 'admin' | 'operator';
  permissions: {
    properties: {
      create: boolean;
      read: boolean;
      update: boolean;
      delete: boolean;
    };
    users: {
      create: boolean;
      read: boolean;
      update: boolean;
      delete: boolean;
    };
    settings: {
      create: boolean;
      read: boolean;
      update: boolean;
      delete: boolean;
    };
  };
  is_active: boolean;
  created_at: string;
}

export default function UserManagement() {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [formData, setFormData] = useState({
    email: '',
    full_name: '',
    password: '',
    confirmPassword: '',
    role: 'operator' as 'admin' | 'operator',
    is_active: true,
    sendInvitation: true,
    permissions: {
      properties: { create: false, read: true, update: false, delete: false },
      users: { create: false, read: false, update: false, delete: false },
      settings: { create: false, read: false, update: false, delete: false }
    }
  });

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await fetch(`${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/admin-auth`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          action: 'get_users'
        })
      });

      if (response.ok) {
        const data = await response.json();
        setUsers(data.users || []);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (user: User) => {
    setEditingUser(user);
    setFormData({
      email: user.email,
      full_name: user.full_name,
      password: '',
      confirmPassword: '',
      role: user.role,
      is_active: user.is_active,
      sendInvitation: false,
      permissions: user.permissions
    });
    setError('');
    setSuccess('');
    setShowModal(true);
  };

  const handleAdd = () => {
    setEditingUser(null);
    setFormData({
      email: '',
      full_name: '',
      password: '',
      confirmPassword: '',
      role: 'operator',
      is_active: true,
      sendInvitation: true,
      permissions: {
        properties: { create: false, read: true, update: false, delete: false },
        users: { create: false, read: false, update: false, delete: false },
        settings: { create: false, read: false, update: false, delete: false }
      }
    });
    setError('');
    setSuccess('');
    setShowModal(true);
  };

  const generateRandomPassword = () => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789!@#$%';
    let password = '';
    for (let i = 0; i < 12; i++) {
      password += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setFormData(prev => ({ 
      ...prev, 
      password: password,
      confirmPassword: password
    }));
  };

  const validateForm = () => {
    // Validar email
    if (!formData.email || !formData.email.includes('@')) {
      setError('Por favor ingrese un email válido');
      return false;
    }

    // Validar nombre
    if (!formData.full_name || formData.full_name.trim().length < 3) {
      setError('El nombre debe tener al menos 3 caracteres');
      return false;
    }

    // Validar contraseña solo para nuevos usuarios o si se está cambiando
    if (!editingUser || formData.password) {
      if (!formData.password || formData.password.length < 8) {
        setError('La contraseña debe tener al menos 8 caracteres');
        return false;
      }

      if (formData.password !== formData.confirmPassword) {
        setError('Las contraseñas no coinciden');
        return false;
      }

      // Validar complejidad de contraseña
      const hasUpperCase = /[A-Z]/.test(formData.password);
      const hasLowerCase = /[a-z]/.test(formData.password);
      const hasNumber = /[0-9]/.test(formData.password);
      
      if (!hasUpperCase || !hasLowerCase || !hasNumber) {
        setError('La contraseña debe contener mayúsculas, minúsculas y números');
        return false;
      }
    }

    return true;
  };

  const sendInvitationEmail = async (email: string, password: string, fullName: string) => {
    try {
      const formData = new FormData();
      formData.append('email', email);
      formData.append('subject', 'Invitación al Panel Administrativo - Grupo FR Venture');
      formData.append('message', `
Hola ${fullName},

Has sido invitado a acceder al Panel Administrativo de Grupo FR Venture.

Tus credenciales de acceso son:
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
Email: ${email}
Contraseña: ${password}
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

Para acceder al panel administrativo:
1. Ve a la página de administración
2. Ingresa tu email y contraseña
3. Por seguridad, te recomendamos cambiar tu contraseña en tu primer acceso

Pasos para cambiar tu contraseña:
1. Inicia sesión con las credenciales proporcionadas
2. Ve a Configuración > Cambiar Contraseña
3. Ingresa tu contraseña actual y tu nueva contraseña

IMPORTANTE: Por favor, guarda esta información en un lugar seguro y no la compartas con nadie.

Si tienes alguna pregunta o problema para acceder, contacta al administrador del sistema.

Saludos,
Equipo Administrativo Grupo FR Venture
      `);

      const response = await fetch('https://readdy.ai/api/form/d4c0punhkbdsb76v7c50', {
        method: 'POST',
        body: formData
      });

      return response.ok;
    } catch (error) {
      console.error('Error sending invitation email:', error);
      return false;
    }
  };

  const handleSave = async () => {
    setError('');
    setSuccess('');

    // Validar formulario
    if (!validateForm()) {
      return;
    }

    setSaving(true);

    try {
      const action = editingUser ? 'update_user' : 'create_user';
      const body: any = {
        action,
        userData: {
          email: formData.email,
          full_name: formData.full_name,
          role: formData.role,
          is_active: formData.is_active,
          permissions: formData.permissions
        }
      };

      // Solo incluir contraseña si se proporcionó
      if (formData.password) {
        body.userData.password = formData.password;
      }

      if (editingUser) {
        body.userId = editingUser.id;
      }

      const response = await fetch(`${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/admin-auth`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(body)
      });

      if (response.ok) {
        // Si es un nuevo usuario y se marcó enviar invitación
        if (!editingUser && formData.sendInvitation && formData.password) {
          const emailSent = await sendInvitationEmail(
            formData.email, 
            formData.password, 
            formData.full_name
          );
          
          if (emailSent) {
            setSuccess('Usuario creado exitosamente. Se ha enviado un email con las credenciales de acceso.');
          } else {
            setSuccess('Usuario creado exitosamente. Sin embargo, hubo un problema al enviar el email de invitación.');
          }
        } else {
          setSuccess(editingUser ? 'Usuario actualizado exitosamente' : 'Usuario creado exitosamente');
        }

        // Esperar un momento para mostrar el mensaje
        setTimeout(() => {
          setShowModal(false);
          fetchUsers();
          setSuccess('');
        }, 2000);
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Error al guardar el usuario');
      }
    } catch (error) {
      console.error('Error saving user:', error);
      setError('Error de conexión. Por favor intente nuevamente.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (confirm('¿Estás seguro de que quieres eliminar este usuario? Esta acción no se puede deshacer.')) {
      try {
        const response = await fetch(`${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/admin-auth`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            action: 'delete_user',
            userId: id
          })
        });

        if (response.ok) {
          fetchUsers();
        }
      } catch (error) {
        console.error('Error deleting user:', error);
      }
    }
  };

  const updatePermission = (module: string, action: string, value: boolean) => {
    setFormData(prev => ({
      ...prev,
      permissions: {
        ...prev.permissions,
        [module]: {
          ...prev.permissions[module as keyof typeof prev.permissions],
          [action]: value
        }
      }
    }));
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="w-8 h-8 animate-spin">
          <i className="ri-loader-4-line text-2xl text-blue-600"></i>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-900">Gestión de Usuarios</h2>
        <Button onClick={handleAdd}>
          <i className="ri-user-add-line mr-2"></i>
          Agregar Usuario
        </Button>
      </div>

      <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Usuario
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Rol
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Estado
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Creado
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Acciones
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {users.map((user) => (
                <tr key={user.id}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                        <i className="ri-user-line text-blue-600"></i>
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900">{user.full_name}</div>
                        <div className="text-sm text-gray-500">{user.email}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      user.role === 'admin' 
                        ? 'bg-purple-100 text-purple-800' 
                        : 'bg-blue-100 text-blue-800'
                    }`}>
                      {user.role === 'admin' ? 'Administrador' : 'Operador'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      user.is_active 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {user.is_active ? 'Activo' : 'Inactivo'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {formatDate(user.created_at)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                    <button
                      onClick={() => handleEdit(user)}
                      className="text-blue-600 hover:text-blue-900 cursor-pointer"
                    >
                      <i className="ri-edit-line"></i>
                    </button>
                    {user.role !== 'admin' && (
                      <button
                        onClick={() => handleDelete(user.id)}
                        className="text-red-600 hover:text-red-900 cursor-pointer"
                      >
                        <i className="ri-delete-bin-line"></i>
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <Modal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        title={editingUser ? 'Editar Usuario' : 'Agregar Usuario'}
      >
        <div className="space-y-6">
          {/* Mensajes de Error y Éxito */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-center">
                <i className="ri-error-warning-line text-red-500 mr-3 text-lg"></i>
                <p className="text-red-700 text-sm font-medium">{error}</p>
              </div>
            </div>
          )}

          {success && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-center">
                <i className="ri-check-circle-line text-green-500 mr-3 text-lg"></i>
                <p className="text-green-700 text-sm font-medium">{success}</p>
              </div>
            </div>
          )}

          {/* Información Básica */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-gray-900">Información Básica</h3>
            
            <Input
              label="Email"
              type="email"
              value={formData.email}
              onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
              placeholder="usuario@ejemplo.com"
              disabled={!!editingUser}
              required
            />

            <Input
              label="Nombre Completo"
              value={formData.full_name}
              onChange={(e) => setFormData(prev => ({ ...prev, full_name: e.target.value }))}
              placeholder="Nombre completo del usuario"
              required
            />

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Rol</label>
              <select
                value={formData.role}
                onChange={(e) => setFormData(prev => ({ ...prev, role: e.target.value as 'admin' | 'operator' }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 pr-8"
              >
                <option value="operator">Operador</option>
                <option value="admin">Administrador</option>
              </select>
            </div>

            <label className="flex items-center">
              <input
                type="checkbox"
                checked={formData.is_active}
                onChange={(e) => setFormData(prev => ({ ...prev, is_active: e.target.checked }))}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
              />
              <span className="ml-2 text-sm text-gray-700">Usuario activo</span>
            </label>
          </div>

          {/* Contraseña */}
          <div className="space-y-4 border-t pt-4">
            <div className="flex justify-between items-center">
              <h3 className="text-lg font-medium text-gray-900">
                {editingUser ? 'Cambiar Contraseña (Opcional)' : 'Contraseña'}
              </h3>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={generateRandomPassword}
              >
                <i className="ri-refresh-line mr-2"></i>
                Generar Automática
              </Button>
            </div>

            <Input
              label="Contraseña"
              type="password"
              value={formData.password}
              onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
              placeholder="Mínimo 8 caracteres"
              required={!editingUser}
            />

            <Input
              label="Confirmar Contraseña"
              type="password"
              value={formData.confirmPassword}
              onChange={(e) => setFormData(prev => ({ ...prev, confirmPassword: e.target.value }))}
              placeholder="Repita la contraseña"
              required={!editingUser}
            />

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <p className="text-xs text-blue-800">
                <i className="ri-information-line mr-1"></i>
                La contraseña debe tener al menos 8 caracteres e incluir mayúsculas, minúsculas y números.
              </p>
            </div>

            {!editingUser && (
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={formData.sendInvitation}
                  onChange={(e) => setFormData(prev => ({ ...prev, sendInvitation: e.target.checked }))}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                />
                <span className="ml-2 text-sm text-gray-700">
                  Enviar email de invitación con credenciales de acceso
                </span>
              </label>
            )}
          </div>

          {/* Permisos */}
          <div className="space-y-4 border-t pt-4">
            <h3 className="text-lg font-medium text-gray-900">Permisos</h3>
            
            {Object.entries(formData.permissions).map(([module, perms]) => (
              <div key={module} className="border rounded-lg p-4">
                <h4 className="font-medium text-gray-900 mb-3 capitalize">
                  {module === 'properties' ? 'Propiedades' : 
                   module === 'users' ? 'Usuarios' : 'Configuración'}
                </h4>
                <div className="grid grid-cols-2 gap-3">
                  {Object.entries(perms).map(([action, value]) => (
                    <label key={action} className="flex items-center">
                      <input
                        type="checkbox"
                        checked={value as boolean}
                        onChange={(e) => updatePermission(module, action, e.target.checked)}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                      />
                      <span className="ml-2 text-sm text-gray-700 capitalize">
                        {action === 'create' ? 'Crear' :
                         action === 'read' ? 'Leer' :
                         action === 'update' ? 'Actualizar' : 'Eliminar'}
                      </span>
                    </label>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="flex justify-end space-x-3 mt-6 pt-6 border-t">
          <Button 
            variant="outline" 
            onClick={() => setShowModal(false)}
            disabled={saving}
          >
            Cancelar
          </Button>
          <Button 
            onClick={handleSave}
            loading={saving}
          >
            <i className={`${editingUser ? 'ri-save-line' : 'ri-user-add-line'} mr-2`}></i>
            {saving ? 'Guardando...' : (editingUser ? 'Actualizar Usuario' : 'Crear Usuario')}
          </Button>
        </div>
      </Modal>
    </div>
  );
}
